import { BottomSheet, Button, Form, Typography, type FormValues } from '@s4l/ds'
import { IconFilter } from '@s4l/icons/IconFilter'
import { IconTrash } from '@s4l/icons/IconTrash'
import { useS4LContext } from '@s4l/react-context'
import { SearchFilters } from '@s4l/stayforlong/property/SearchFilters'
import { cn, type UtilityClassNames } from '@s4l/utility-css'
import React from 'react'
import styles from './index.module.css'

interface IFilterLayoutProps {
  children: React.ReactNode
  onSubmit?: (form: FormValues) => void
}

export const FilterLayout = ({ children, onSubmit }: IFilterLayoutProps) => {
  const { i18n } = useS4LContext()
  const [isOpen, setIsOpen] = React.useState(false)

  const onSubmitInternal = (form: FormValues) => {
    setIsOpen(false)
    onSubmit?.(form)
  }

  const onClearAll = () => {
    setIsOpen(false)
    onSubmit?.(SearchFilters.default().toPrimitives())
  }

  return (
    <>
      <BottomSheet open={isOpen} onClose={() => setIsOpen(false)}>
        <BottomSheet.Trigger asChild>
          <Button
            className={['display-flex', 'desktop:display-none'].join(' ') as UtilityClassNames}
            variant="outlined-rounded"
            leadingIcon={<IconFilter size={16} />}
            data-testid="filter"
            size="small"
            fullWidth
            onClick={() => setIsOpen(true)}
          >
            <div className={styles.filtersHeader}>
              {i18n.formatMessage({ id: 'global.filters' })}
              <Form.CountBadge />
            </div>
          </Button>
        </BottomSheet.Trigger>
        <BottomSheet.Content
          scroll
          title={
            <div className={styles.filtersHeader}>
              {i18n.formatMessage({ id: 'global.filters' })}
              <Form.CountBadge size="medium" />
            </div>
          }
          footer={
            <>
              <Form.ClearButton
                data-testid="buttonReset"
                fullWidth
                hideTotalCount
                onClick={onClearAll}
              >
                {i18n.formatMessage({ id: 'global.clear-all' })}
              </Form.ClearButton>
              <Form.SubmitButton fullWidth onClick={onSubmitInternal}>
                {i18n.formatMessage({ id: 'button.apply' })}
              </Form.SubmitButton>
            </>
          }
        >
          {children}
        </BottomSheet.Content>
      </BottomSheet>
      <div className={cn(['display-none', 'desktop:display-block', 'mb-16'])}>
        <div className={styles.filtersHeader}>
          <Typography as="h6" variant="title" size="xs">
            {i18n.formatMessage({ id: 'global.filters' })}
          </Typography>
          <Form.CountBadge size="medium" />
          <div className={styles.headerButtonReset}>
            <Form.ClearButton data-testid="headerButtonReset" small autoHide icon={<IconTrash />}>
              {i18n.formatMessage({ id: 'global.clear-all' })}
            </Form.ClearButton>
          </div>
        </div>
        {children}
      </div>
    </>
  )
}

FilterLayout.displayName = 'SearchFilters.FilterLayout'
export default FilterLayout
